import {Component, OnInit, inject, output, input, effect} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule} from "@angular/forms";
import {ViewMode, ProjectStatus, Right} from "../../../interfaces";
import {DataService} from "../../../../services/data.service";
import {FormService} from "../../../../services/form.service";
import Swal from "sweetalert2";
import {ProjectStatusPipe} from "../../../../pipes/project-status.pipe";
import {AccessService} from "../../../../services/access.service";
import {ToastrService} from "ngx-toastr";
import {NgIf, NgFor, DecimalPipe, DatePipe} from '@angular/common';
import {TooltipModule} from 'ngx-bootstrap/tooltip';

@Component({
    selector: 'app-research-costs',
    templateUrl: './research-costs.component.html',
    styleUrl: './research-costs.component.scss',
    imports: [NgIf, TooltipModule, FormsModule, ReactiveFormsModule, NgFor, DecimalPipe, DatePipe]
})
export class ResearchCostsComponent implements OnInit {

    accessService = inject(AccessService);
    formService = inject(FormService);
    private dataService = inject(DataService);
    private fb = inject(UntypedFormBuilder);
    private projectStatusPipe = inject(ProjectStatusPipe);
    private toastrService = inject(ToastrService);

    readonly PID = input.required<any>();
    readonly reloadTrigger = input<number>(0);

    readonly changeProject = output();
    readonly changeViewMode = output<boolean>();

    readonly ViewMode = ViewMode;
    readonly Right = Right;

    costTypeList: any[];
    viewMode!: ViewMode;
    project: any;
    form!: UntypedFormGroup;
    sumAmountPlanned: number;

    private reloadEffect = effect(() => {
        if (this.reloadTrigger() > 0) {
            this.getProject();
        }
    })

    ngOnInit(): void {
        this.viewMode = ViewMode.VIEW;

        this.form = this.fb.group({
            id: [null],
            cost_budget: [null],

            costs: this.fb.array([]),
            budget: this.fb.array([]),
            invoices: this.fb.array([]),
        });

        if (this.PID() !== '0') {
            this.getProject();
        }

        this.dataService.request("CostType/listAll").subscribe(response => {
            this.costTypeList = response.cost_types;
        })
    }

    getProject(): void {
        this.dataService.request('Research/get', {
            PID: this.PID()
        }).subscribe(response => {
            this.project = response.project;

            this.patchForm();
        });
    }

    patchForm(): void {
        this.form.patchValue({
            id: this.project.id,
            cost_budget: this.project.cost_budget,
        });

        this.formService.getFormArray(this.form, 'costs').clear();
        this.project.costs.forEach(item => {
            if (item.cost_type == null) item.cost_type = 'Nicht gesetzt';
            this.addFormItemCosts(item, 'costs');
        });
        this.calculateSum();

        this.formService.getFormArray(this.form, 'budget').clear();
        this.project.budget.forEach(item => {
            this.addFormItemBudget(item, 'budget');
        });

        this.formService.getFormArray(this.form, 'invoices').clear();
        this.project.invoices.forEach(item => {
            this.addFormItemInvoices(item, 'invoices');
        });
    }

    addFormItemCosts(data: any = {}, control): void {
        this.formService.getFormArray(this.form, control).push(
            this.fb.group({
                id: [data.id || null],
                id_project: [data.id_project || this.project.id],
                id_cost_type: [data.id_cost_type || ''],
                description: [data.description || ''],
                amount: [data.amount || '', Validators.required],
                _deleted: [false],
                cost_type: [data.cost_type || ''],
            })
        );
    }

    addFormItemBudget(data: any = {}, control): void {
        this.formService.getFormArray(this.form, control).push(
            this.fb.group({
                id: [data.id || null],
                id_project: [data.id_project || this.project.id],
                id_approver: [data.id_approver || null],
                creator: [data.creator || null],
                approver: [data.approver || ''],
                amount: [data.amount || '', Validators.required],
                created_at: [data.created_at || new Date()],
                approved_at: [data.approved_at || null],
                comment: [data.comment || ''],
                _deleted: [false],
            })
        );
    }

    addFormItemInvoices(data: any = {}, control): void {
        this.formService.getFormArray(this.form, control).push(
            this.fb.group({
                id: [data.id || null],
                id_project: [data.id_project || this.project.id],
                amount: [data.amount || '', Validators.required],
                _deleted: [false],
            })
        );
    }

    setMode(viewMode: ViewMode): void {
        this.viewMode = viewMode;
        if (this.viewMode == ViewMode.EDIT) {
            this.changeViewMode.emit(true);
        } else {
            this.changeViewMode.emit(false);
        }
    }

    update(): void {
        if (![ProjectStatus.REVISE].includes(this.project.status)) {
            this.dataService.request('Research/updateCosts', {
                project: this.form.getRawValue(),
            }).subscribe(() => {
                this.toastrService.success('Erfolgreich gespeichert')
                this.setMode(ViewMode.VIEW);
                this.getProject();
                this.changeProject.emit();
            });
        } else {
            Swal.fire({
                title: 'Sicher?',
                text: 'Der Status wird auf "' + this.projectStatusPipe.transform(ProjectStatus.IN_ELABORATION) + '" gesetzt.',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#DD6B55',
                confirmButtonText: 'Ja',
                cancelButtonText: 'Nein'
            }).then(result => {
                if (result.value) {
                    this.dataService.request('Research/setStatus', {
                        PID: this.PID(),
                        status: ProjectStatus.IN_ELABORATION,
                    }).subscribe(() => {
                        this.dataService.request('Research/updateCosts', {
                            project: this.form.getRawValue(),
                        }).subscribe(() => {
                            this.toastrService.success('Erfolgreich gespeichert')
                            this.setMode(ViewMode.VIEW);
                            this.getProject();
                            this.changeProject.emit();
                        });
                    });
                }
            });
        }
    }

    cancelEdit(): void {
        this.patchForm();
        this.setMode(ViewMode.VIEW);
    }

    markEntryAsDeleted(entry, calculateSum = false): void {
        entry.patchValue({
            _deleted: true,
        });
        for (const key in entry.controls) {
            entry.get(key).clearValidators();
            entry.get(key).updateValueAndValidity();
        }
        if (calculateSum) this.calculateSum();
    }

    getLengthOfFormArray(name): number {
        return this.formService.getFormArray(this.form, name).controls.filter(control => {
            return control.value._deleted == false;
        }).length;
    }

    calculateSum(): void {
        this.sumAmountPlanned = this.formService.getFormArray(this.form, 'costs').controls.reduce((carry, next) => {
            return carry + (next.value._deleted ? 0 : parseInt(next.value.amount));
        }, 0);
    }

    approveEntry(entry) {
        entry.patchValue({
            id_approver: this.accessService.user.id,
            approved_at: new Date(),
            approver: this.accessService.user.name,
        })
    }
}
